import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import useBriefContext from '../../Hooks/Brief/useBriefContext'
import { StylesQuery, StylesDocument } from '../FormSteps/Styles/StylesInput'
import { useQuery } from 'urql'
import throwGQLError from '../../Utilities/Urql/Errors/throwGQLError'
import InputView from '../FormSteps/InputView'
import { useFacilitiesQuery } from '../FormSteps/Facilities/Base/Facilities/FacilitiesInput.generated'
import FormPage from '../../Components/Form/Page/FormPage'
import LoadingSpinner from '../../Components/LoadingSpinner'
import './SummaryPage.less'
import BizSummaryList from './Biz/SummaryList'
import isBizBrief from '../../Logic/Form/Sequences/Biz/isBizBrief'
import { requiredGuestAttrs } from '../../Logic/Form/Sequences/Biz/sequence'
import isGuestAuthenticated from '../../Logic/Guest/isGuestAuthenticated'
import LFSummaryList from './LF/SummaryList'
import shouldSkipRecommendations from '../../Logic/Form/Sequences/shouldSkipRecommendations'
import useRegisterUser from '../../Hooks/Brief/useRegisterUser'
import useSubmitBrief from '../../Hooks/Brief/useSubmitBrief'

export const SummaryPage: InputView = ({ path }) => {
  const { brief } = useBriefContext()
  const isBizSummary = isBizBrief(brief)
  const skipRecs = !isBizSummary || shouldSkipRecommendations(brief)
  const navigate = useNavigate()

  const [shouldRegisterUser, setShouldRegisterUser] = useState(false)
  const [shouldSubmitBrief, setShouldSubmitBrief] = useState(false)

  const { registering, registered } = useRegisterUser({ skip: !shouldRegisterUser })
  const { loading: submitting } = useSubmitBrief({ skip: !shouldSubmitBrief, venueRequests: [] })

  useEffect(() => {
    if (!registered || !shouldRegisterUser) return
    setShouldSubmitBrief(true)
  }, [registered])

  const onSkipRecs = () => {
    if (!brief.guest?.id) {
      const hasRequiredGuestAttrs = requiredGuestAttrs.every((attr) => !!brief.guest?.[attr])
      if (!hasRequiredGuestAttrs) return navigate('/user')
      return setShouldRegisterUser(true)
    }
    if (isBizSummary && !isGuestAuthenticated(brief)) return navigate('/returning-user')
    setShouldSubmitBrief(true)
  }

  const [{ fetching: fetchingStyles, error: stylesError, data: stylesData }] =
    useQuery<StylesQuery>({
      query: StylesDocument,
      context: useMemo(() => ({ requestPolicy: 'cache-first' }), []),
    })
  const [{ fetching: fetchingFeatures, error: facilityError, data: facilitiesData }] =
    useFacilitiesQuery({
      pause: brief.collection === undefined,
      requestPolicy: 'cache-first',
      variables: { id: brief.collection ?? '' },
    })

  const { getStyles: styles } = stylesData ?? { getStyles: [] }
  const { features: facilities } = facilitiesData?.collection ?? { features: [] }
  const error = stylesError ?? facilityError

  if (error) throwGQLError(error)

  const SummaryList = isBizSummary ? BizSummaryList : LFSummaryList

  const showLoadingState = registering || submitting

  return (
    <>
      {showLoadingState && (
        <LoadingSpinner
          fullPage={true}
          accessibleText={registering ? 'Creating your account...' : 'Submitting your enquiry...'}
        />
      )}
      <FormPage
        path={path}
        canContinue={true}
        onContinue={() => undefined}
        afterContinue={skipRecs ? onSkipRecs : undefined}
        metaTitle='Event summary - HeadBox'
        heading={
          isBizSummary
            ? 'Please check your enquiry details'
            : 'One last check before we share your enquiry'
        }
        submitText={skipRecs ? 'Submit' : undefined}
        className={`summary-page ${isBizSummary ? 'biz' : 'lf'}`}
        hidden={showLoadingState}>
        <SummaryList
          facilities={fetchingFeatures ? undefined : facilities}
          styles={fetchingStyles ? undefined : styles}
        />
      </FormPage>
    </>
  )
}

export default SummaryPage
